/** Public web origin used for shareable invite links. */
export const INVITE_WEB_BASE = 'https://afroyap-8bfb1.web.app';

/**
 * Random 12-character invite code (A–Z, 0–9). Stored on the tribe doc as
 * `inviteCode` and echoed back in the link's ?invite= param, which
 * TribeInvitePage checks before treating a visitor as invited.
 */
export function generateInviteCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

/** The shareable link for a tribe invite. */
export function buildTribeInviteLink(tribeId: string, inviteCode: string): string {
  return `${INVITE_WEB_BASE}/tribes/${tribeId}?invite=${inviteCode}`;
}

/** The shareable link for a forum invite. */
export function buildForumInviteLink(forumId: string, inviteCode: string): string {
  return `${INVITE_WEB_BASE}/forums/${forumId}?invite=${inviteCode}`;
}

/**
 * The shareable link for a room.
 *
 * No invite code, unlike tribes and forums: a room is short-lived and already
 * capped at 15 people, so the room id IS the invite. There is nothing for a code
 * to protect that the capacity limit and the expiry do not already handle.
 */
export function buildRoomInviteLink(roomId: string): string {
  return `${INVITE_WEB_BASE}/rooms/${roomId}`;
}

/** Which kind of group a link points at. */
export type GroupKind = 'tribe' | 'forum';

/**
 * One builder for either kind, so a caller that already knows the kind does not
 * have to branch. Note the plural paths: /tribes/:id and /forums/:id are the
 * PUBLIC landing pages, distinct from the in-app /tribe/:id and /forum/:id
 * screens. A shared link has to survive being opened by someone with no
 * account, which is what the landing pages are for.
 */
export function buildGroupInviteLink(kind: GroupKind, groupId: string, inviteCode: string): string {
  return kind === 'tribe'
    ? buildTribeInviteLink(groupId, inviteCode)
    : buildForumInviteLink(groupId, inviteCode);
}

/** Firestore collection backing each kind. */
export const GROUP_COLLECTION: Record<GroupKind, string> = {
  tribe: 'tribes',
  forum: 'forums',
};

/** In-app screen for a group: where an invite lands once the visitor is signed in. */
export function buildGroupScreenPath(kind: GroupKind, groupId: string, inviteCode?: string): string {
  const base = kind === 'tribe' ? `/tribe/${groupId}` : `/forum/${groupId}`;
  return inviteCode ? `${base}?invite=${inviteCode}` : base;
}

const PENDING_INVITE_KEY = 'pendingGroupInvite';
/** Pre-generalisation key. Read on the way out so a link opened mid-upgrade still lands. */
const LEGACY_TRIBE_INVITE_KEY = 'pendingTribeInvite';

/**
 * Remember where a visitor was headed before they got sent to log in or sign up.
 * Consumed once auth completes, which is what stops an invite from dumping the
 * new member on Explore with no idea what they clicked.
 */
export function stashPendingGroupInvite(kind: GroupKind, groupId: string, inviteCode?: string): void {
  try {
    sessionStorage.setItem(PENDING_INVITE_KEY, JSON.stringify({ kind, groupId, inviteCode: inviteCode || '' }));
  } catch { /* private mode / storage disabled: the invite just doesn't survive login */ }
}

/** Read-and-clear the pending invite, returning the in-app path or null. */
export function consumePendingGroupInvite(): string | null {
  try {
    const raw = sessionStorage.getItem(PENDING_INVITE_KEY);
    if (raw) {
      sessionStorage.removeItem(PENDING_INVITE_KEY);
      const { kind, groupId, inviteCode } = JSON.parse(raw);
      if (groupId) return buildGroupScreenPath(kind === 'forum' ? 'forum' : 'tribe', String(groupId), inviteCode);
    }
    const legacy = sessionStorage.getItem(LEGACY_TRIBE_INVITE_KEY);
    if (legacy) {
      sessionStorage.removeItem(LEGACY_TRIBE_INVITE_KEY);
      const { tribeId, inviteCode } = JSON.parse(legacy);
      if (tribeId) return buildGroupScreenPath('tribe', String(tribeId), inviteCode);
    }
  } catch { /* malformed or unreadable: fall through to the caller's default */ }
  return null;
}
